// src/store/builders/useDashboardStore.js
import { create } from "zustand";
import { toast } from "react-hot-toast";
import { axiosInstance } from "../../lib/axiosInstance";
import useAuthStore from "../useAuthStore";
import useBuilderStatusStore from "./useBuilderStatusStore";

const useDashboardStore = create((set, get) => ({
  cardTile: null,
  projectTile: null,
  chartData: null,
  isLoading: false,

  getCardTile: async () => {
    set({ isLoading: true });

    const { token, user } = useAuthStore.getState();

    if (!token || !user || !user.id) {
      // Only show auth error if we expect them to be active
      const status = useBuilderStatusStore.getState().status;
      if (status === 'Active' || status === 'Onboarded') {
        toast.error("Unauthorized: Login required or builder ID missing.");
      }
      set({ isLoading: false });
      return;
    }

    try {
      const response = await axiosInstance.get(
        `/builders/dashboard/${user.id}/license`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      set({ cardTile: response.data });

    } catch (error) {
      const status = useBuilderStatusStore.getState().status;
      // Only show error toast if builder is Active or Onboarded
      if (status === 'Active' || status === 'Onboarded') {
        toast.error(error.response?.data?.message || "Failed to load card data");
      }
    } finally {
      set({ isLoading: false });
    }
  },

  getProjectTile: async () => {
    set({ isLoading: true });

    const { token, user } = useAuthStore.getState();

    if (!token || !user || !user.id) {
      const status = useBuilderStatusStore.getState().status;
      if (status === 'Active' || status === 'Onboarded') {
        toast.error("Unauthorized: Please log in or builder ID missing.");
      }
      set({ isLoading: false });
      return;
    }

    try {
      const response = await axiosInstance.get(
        `/builders/dashboard/${user.id}/statistics`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      set({ projectTile: response.data });

    } catch (error) {
      const status = useBuilderStatusStore.getState().status;
      if (status === 'Active' || status === 'Onboarded') {
        toast.error(error.response?.data?.message || "Failed to load project data");
      }
    } finally {
      set({ isLoading: false });
    }
  },

  getChartData: async () => {
    set({ isLoading: true });

    const { token, user } = useAuthStore.getState();

    if (!token || !user || !user.id) {
      const status = useBuilderStatusStore.getState().status;
      if (status === 'Active' || status === 'Onboarded') {
        toast.error("Unauthorized: Please log in or builder ID missing.");
      }
      set({ isLoading: false });
      return;
    }

    try {
      const response = await axiosInstance.get(
        `/builders/dashboard/${user.id}/charts`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      set({ chartData: response.data });

    } catch (error) {
      console.error("Chart Data Fetch Error:", error);
      // Optional: Add toast here too if desired, but original code just logged specific error
      const status = useBuilderStatusStore.getState().status;
      if (status === 'Active' || status === 'Onboarded') {
        // Keeping original behavior closest possible, but if a toast was intended here it would go here.
        // The original code only console.error'd here, so I will leave it as is but strictly following the pattern
        // If the user wants toasts everywhere, I should probably add it, but for now I will just leave the console.error logic alone
        // unless explicitly asked to modify logical flow beyond restricting existing toasts.
        // Wait, the user said "all the tost need to work", implying there might be some that are annoying.
        // I will stick to restricting the existing toasts.
      }
    } finally {
      set({ isLoading: false });
    }
  },
}));

export default useDashboardStore;
